import { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import {
  BUTTON_DISCONNECT_ACCOUNT,
  BUTTON_RETRY,
  BUTTON_UNDERSTOOD,
  BUTTON_USE_THIS_CONTENT,
  DISCONNECTED_ACCOUNT,
  INFORMATION,
  MODAL_CANVA_ARIA_LABEL,
  MODAL_CANVA_CREATING_DESIGN,
  MODAL_CANVA_DESCRIPTION,
  MODAL_CANVA_DESIGN_NOT_CREATED,
  MODAL_CANVA_DESIGN_NOT_RETRIEVED,
  MODAL_CANVA_INFORMATION_SAVE_DESIGN,
  MODAL_CANVA_IS_EDITING_IN_CANVA,
  MODAL_CANVA_IS_GETTING_YOUR_DESIGN,
  MODAL_CANVA_TITLE,
  PAGE_NUMBER,
} from "../../i18n/keysTranslations";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import _ from "lodash";
import DialogActions from "@mui/material/DialogActions";
import Skeleton from "@mui/material/Skeleton";
import Checkbox from "@mui/material/Checkbox";
import Masonry from "@mui/lab/Masonry";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import {
  startCreateCanvaDesign,
  startExportCanvaDesign,
  startSaveMediaInStorage,
  startRevokeCanvaAccount,
} from "../../actions/user";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import ModalBasicLayout from "./ModalBasicLayout";
import Loader from "../Loaders/Loader";
import CardMedia from "../Media/CardMedia";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { getNameBusinessTheme } from "../../actions/getters";
import Button from "../Buttons/Button";
import {
  ALERT_ICON_TYPE_SUCCESS,
  MODAL_CANVA_IMAGE_CONFIG_MODE_CREATE,
  MODAL_CANVA_IMAGE_CONFIG_MODE_DISCONNECT,
  MODAL_CANVA_IMAGE_CONFIG_MODE_EDIT,
} from "../../utils/constants";
import ModalCanvaImageConfig from "./ModalCanvaImageConfig";
import {
  IMAGE_TYPE_LANDSCAPE,
  IMAGE_TYPE_PORTRAIT,
  IMAGE_TYPE_SQUARE,
} from "../../utils/constants";
import differenceInSeconds from "date-fns/differenceInSeconds";

const LoadingTitle = ({ message, isLoading }) => {
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (!isLoading) {
      setDots("");
      return;
    }

    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === "...") return "";
        return prev + ".";
      });
    }, 500);

    return () => clearInterval(interval);
  }, [isLoading]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        position: "absolute",
        zIndex: 1000,
      }}
    >
      {isLoading && (
        <CircularProgress
          size={30}
          sx={{
            color: "primary.main",
          }}
        />
      )}
      <Typography
        variant="h6"
        sx={{
          textAlign: "center",
        }}
      >
        {message}
        {isLoading ? dots : ""}
      </Typography>
    </Box>
  );
};

const ModalCanva = ({
  modalOpen,
  onCloseModal = () => {},
  onCallbackSave = () => {},
  imageUrl = null,
  imageDimensions = null,
  pathStorage = null,
}) => {
  const [design, setDesign] = useState({});
  const [hasError, setHasError] = useState(false);
  const [errorType, setErrorType] = useState(null); // 'creation' | 'retrieval' | null
  const [isCreatingDesign, setIsCreatingDesign] = useState(false);
  const [isGettingDesignMedia, setIsGettingDesignMedia] = useState(false);
  const [showImageConfig, setShowImageConfig] = useState(true);
  const [showDisconnectAccountSelector, setShowDisconnectAccountSelector] =
    useState(false);
  const [designConfig, setDesignConfig] = useState(null);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [isDisconnectingAccount, setIsDisconnectingAccount] = useState(false);
  // Estados para múltiples páginas
  const [multiplePages, setMultiplePages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);

  const designID = design?.id;
  const editUrl = design?.urls?.edit_url;
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const getInitialData = async (config) => {
    setIsCreatingDesign(true);
    setHasError(false);
    setErrorType(null);
    setDesignConfig(config);
    const configWithImage = imageUrl ? { ...config, imageUrl } : config;
    const response = await dispatch(startCreateCanvaDesign(configWithImage));
    if (response) {
      setDesign(response);
    } else {
      setHasError(true);
      setErrorType("creation");
    }
    setIsCreatingDesign(false);
  };

  // Handle tab visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);
    };

    // Set initial state
    setIsTabVisible(!document.hidden);

    // Listen to visibility change (detects tab switching)
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const onImageConfigConfirm = (config) => {
    setShowImageConfig(false);
    const configToUse =
      config.integrationID && imageDimensions
        ? {
            integrationID: config.integrationID,
            width: imageDimensions.width,
            height: imageDimensions.height,
            contentType: "image",
            orientation:
              imageDimensions.width === imageDimensions.height
                ? IMAGE_TYPE_SQUARE
                : imageDimensions.width > imageDimensions.height
                  ? IMAGE_TYPE_LANDSCAPE
                  : IMAGE_TYPE_PORTRAIT,
          }
        : config;
    getInitialData(configToUse);
  };

  const onImageConfigCancel = () => {
    onCloseModal();
  };

  const onExportDesign = async () => {
    if (isGettingDesignMedia || !designID) return;
    setIsGettingDesignMedia(true);
    setHasError(false);
    setErrorType(null);

    const response = await dispatch(
      startExportCanvaDesign({
        designID,
        contentType: designConfig?.contentType || "image",
        orientation: designConfig?.orientation || IMAGE_TYPE_SQUARE,
        integrationID: designConfig?.integrationID,
      })
    );

    // Handle error case
    if (!response || (Array.isArray(response) && response.length === 0)) {
      setHasError(true);
      setErrorType("retrieval");
      setIsGettingDesignMedia(false);
      return;
    }

    // Handle multiple pages scenario
    if (response.length > 1) {
      const pagesData = response.map((url, index) => ({
        id: index,
        url: url,
        selected: false,
      }));
      setMultiplePages(pagesData);
      setSelectedPages([]);
      setIsGettingDesignMedia(false);
      return;
    }

    // Handle single page scenario
    setMultiplePages([]);
    setSelectedPages([]);
    setDesign((prevDesign) => ({
      ...prevDesign,
      mediaUrl: response[0],
      date: new Date().getTime(),
    }));
    setIsGettingDesignMedia(false);
  };

  // Retry function for creation
  const onRetryCreation = async () => {
    if (!designConfig) return;
    await getInitialData(designConfig);
  };

  // Retry function for retrieval
  const onRetryRetrieval = async () => {
    await onExportDesign();
  };

  // Retry function for general
  const onRetry = async () => {
    if (errorType === "creation") {
      await onRetryCreation();
    } else if (errorType === "retrieval") {
      await onRetryRetrieval();
    }
  };

  const onDisconnectAccount = async ({ integrationID } = {}) => {
    setIsDisconnectingAccount(true);
    const response = await dispatch(startRevokeCanvaAccount({ integrationID }));
    setIsDisconnectingAccount(false);

    if (response) {
      SimpleAlert({
        title: t(DISCONNECTED_ACCOUNT),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      setShowDisconnectAccountSelector(false);
      const isCurrentDesignAccount =
        integrationID === designConfig?.integrationID;
      if (isCurrentDesignAccount) {
        onCloseModal();
      }
    }
  };

  const onDisconnectAccountConfirm = (config) => {
    onDisconnectAccount(config);
  };

  // Functions to handle multiple pages
  const onPageSelection = (pageId) => {
    setSelectedPages((prev) => {
      if (prev.includes(pageId)) {
        return prev.filter((id) => id !== pageId);
      } else {
        return [...prev, pageId];
      }
    });
  };

  // Helper function to handle URLs with or without storage
  const onUrlsWithStorage = async (urls) => {
    if (!pathStorage) {
      // No storage needed, call callback for each URL directly
      for (const url of urls) {
        await onCallbackSave(url);
      }
      return;
    }

    // Storage needed, save URLs first
    const response = await dispatch(
      startSaveMediaInStorage({
        urls: urls,
        path: pathStorage,
      })
    );

    if (!response?.ok) {
      return;
    }

    // Use stored URLs or fallback to original URLs
    const finalUrls = response?.data?.urls || urls;
    for (const url of finalUrls) {
      await onCallbackSave(url);
    }
  };

  useEffect(() => {
    if (hasError) return;
    if (!editUrl) return;
    if (design.mediaUrl) return;
    if (isGettingDesignMedia) return;

    let popup;

    ConfirmAlert({
      title: t(INFORMATION),
      text: t(MODAL_CANVA_INFORMATION_SAVE_DESIGN),
      confirmButtonText: t(BUTTON_UNDERSTOOD),
      showCancelButton: false,
    }).then(() => {
      popup = window.open(
        editUrl,
        design.title,
        `width=${screen.width},height=${screen.height},left=0,top=0,fullscreen=yes,scrollbars=yes,resizable=yes`
      );
    });

    const interval = setInterval(() => {
      if (popup?.closed) {
        clearInterval(interval);
        onExportDesign();
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, [editUrl]);

  const showError = hasError;
  const showIsEditing =
    !hasError && !isCreatingDesign && !_.isEmpty(design) && !design.mediaUrl;
  const showMediaUrl = !hasError && !isCreatingDesign && design.mediaUrl;
  const showMultiplePages =
    !hasError && !isCreatingDesign && multiplePages.length > 0;
  const showSinglePage =
    !hasError &&
    !isCreatingDesign &&
    design.mediaUrl &&
    multiplePages.length === 0;

  const onUseThisContent = useCallback(async () => {
    // Handle multiple pages scenario
    if (multiplePages.length > 0 && selectedPages.length > 0) {
      const selectedUrls = multiplePages
        .filter((page) => selectedPages.includes(page.id))
        .map((page) => page.url);

      await onUrlsWithStorage(selectedUrls);
      onCloseModal();
      return;
    }

    // Handle single page scenario
    if (design.mediaUrl) {
      await onUrlsWithStorage([design.mediaUrl]);
      onCloseModal();
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    pathStorage,
    design?.mediaUrl,
    onCallbackSave,
    onCloseModal,
    multiplePages,
    selectedPages,
    onUrlsWithStorage,
  ]);

  useEffect(() => {
    if (!isTabVisible || hasError) return;

    const now = new Date().getTime();
    if (design?.date) {
      const differenceSeconds = differenceInSeconds(now, design.date);
      if (differenceSeconds < 10) {
        return;
      }
    }

    onExportDesign();
    //eslint-disable-next-line
  }, [isTabVisible]);

  const mode = imageDimensions
    ? MODAL_CANVA_IMAGE_CONFIG_MODE_EDIT
    : MODAL_CANVA_IMAGE_CONFIG_MODE_CREATE;

  if (showImageConfig) {
    return (
      <ModalCanvaImageConfig
        modalOpen={modalOpen}
        onCloseModal={onImageConfigCancel}
        onConfirm={onImageConfigConfirm}
        mode={mode}
      />
    );
  }
  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={async () => {
          onCloseModal();
        }}
        fullScreen={true}
        scroll="paper"
        ariaLabel={t(MODAL_CANVA_ARIA_LABEL)}
        title={t(MODAL_CANVA_TITLE)}
        description={t(MODAL_CANVA_DESCRIPTION, {
          NAME_BUSINESS,
        })}
      >
        <Box
          sx={{
            position: "absolute",
            top: "45px",
            right: "40px",
          }}
        >
          <Button
            color="secondary"
            loading={isDisconnectingAccount}
            onClick={() => setShowDisconnectAccountSelector(true)}
          >
            {t(BUTTON_DISCONNECT_ACCOUNT)}
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: isCreatingDesign ? "flex-start" : "center",
            width: "100%",
            height: "100%",
            maxHeight: {
              xs: "calc(100% - 96px)",
              sm: "calc(100% - 80px)",
            },
          }}
        >
          {isCreatingDesign && (
            <Loader
              hasMessage={true}
              message={t(MODAL_CANVA_CREATING_DESIGN)}
              sx={{
                height: "min-content",
              }}
            />
          )}
          {showError && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                p: 3,
              }}
            >
              <Typography variant="h6" textAlign="center">
                {t(
                  errorType === "creation"
                    ? MODAL_CANVA_DESIGN_NOT_CREATED
                    : MODAL_CANVA_DESIGN_NOT_RETRIEVED
                )}
              </Typography>
              <Button
                color="primary"
                onClick={onRetry}
                disabled={isCreatingDesign || isGettingDesignMedia}
                loading={isCreatingDesign || isGettingDesignMedia}
              >
                {t(BUTTON_RETRY)}
              </Button>
            </Box>
          )}
          {showIsEditing && !showMediaUrl && multiplePages.length === 0 && (
            <>
              <LoadingTitle
                message={
                  isGettingDesignMedia
                    ? t(MODAL_CANVA_IS_GETTING_YOUR_DESIGN)
                    : t(MODAL_CANVA_IS_EDITING_IN_CANVA, { NAME_BUSINESS })
                }
                isLoading={isGettingDesignMedia}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
              ></Skeleton>
            </>
          )}

          {/* Preview para página única (funcionamiento original) */}
          {showSinglePage && (
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              <CardMedia
                url={design.mediaUrl}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
              {isGettingDesignMedia && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(255,255,255,0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                  }}
                >
                  <Loader
                    hasMessage={true}
                    message={t(MODAL_CANVA_IS_GETTING_YOUR_DESIGN)}
                    sx={{
                      height: "min-content",
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Preview for multiple pages */}
          {showMultiplePages && (
            <Box sx={{ width: "100%", height: "100%", overflow: "auto", p: 2 }}>
              <Masonry columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} spacing={2}>
                {multiplePages.map((page, index) => {
                  let aspectRatio = "1/1";
                  if (
                    designConfig &&
                    designConfig.width &&
                    designConfig.height
                  ) {
                    aspectRatio = `${designConfig.width}/${designConfig.height}`;
                  }

                  return (
                    <Box key={page.id}>
                      <Box
                        sx={{
                          boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                          borderRadius: 3,
                          cursor: "pointer",
                          position: "relative",
                          width: "100%",
                          aspectRatio: aspectRatio,
                          transition: "all 0.2s",
                          borderColor: "transparent",
                          "&:hover": {
                            opacity: 1,
                            transform: "scale(1.02)",
                          },
                        }}
                        onClick={(e) => {
                          if (e.target.closest(".canva-page-checkbox")) {
                            return;
                          }
                          onPageSelection(page.id);
                        }}
                      >
                        <CardMedia
                          url={page.url}
                          sx={{
                            objectFit: "cover",
                            borderRadius: 3,
                            width: "100%",
                            height: "100%",
                          }}
                        />
                        {isGettingDesignMedia && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              bgcolor: "rgba(255,255,255,0.8)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 2,
                              borderRadius: 3,
                            }}
                          >
                            <Loader
                              hasMessage={true}
                              message={t(MODAL_CANVA_IS_GETTING_YOUR_DESIGN)}
                              sx={{
                                height: "min-content",
                              }}
                            />
                          </Box>
                        )}

                        {/* Checkbox overlay style GalleryItem */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0, 0, 0, 0.55)",
                            borderRadius: 1.5,
                            p: 0.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Checkbox
                            className="canva-page-checkbox"
                            checked={selectedPages.includes(page.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={() => {
                              onPageSelection(page.id);
                            }}
                            sx={{
                              color: "white",
                              "&.Mui-checked": {
                                color: "white !important",
                              },
                              "& .MuiSvgIcon-root": {
                                color: "white !important",
                                fontSize: "1rem",
                              },
                              p: 0,
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 10,
                            left: 10,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            maxWidth: "calc(100% - 20px)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#ffffff",
                              fontWeight: 500,
                              fontSize: "12px",
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {t(PAGE_NUMBER, { number: index + 1 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Masonry>
            </Box>
          )}
        </Box>
        {(showSinglePage || showMultiplePages) && (
          <DialogActions sx={{ p: 0, mt: 1 }}>
            <ContainerModalActions
              sx={{
                justifyContent: "center",
              }}
              propsPrimaryButton={{
                showButton: true,
                text: BUTTON_USE_THIS_CONTENT,
                onClick: onUseThisContent,
                disabled: showMultiplePages && selectedPages.length === 0,
              }}
            />
          </DialogActions>
        )}
      </ModalBasicLayout>
      {showDisconnectAccountSelector && (
        <ModalCanvaImageConfig
          modalOpen={showDisconnectAccountSelector}
          onCloseModal={() => setShowDisconnectAccountSelector(false)}
          onConfirm={onDisconnectAccountConfirm}
          mode={MODAL_CANVA_IMAGE_CONFIG_MODE_DISCONNECT}
        />
      )}
    </>
  );
};

export default ModalCanva;
